import type { Control, UseFormSetValue } from 'react-hook-form';
import type { ScheduleType } from '@circuitry-ai/doc-data';

export type ScheduleEndCondition = 'never' | 'specific_date';
export type ScheduleConfigurationState = 'ENABLED' | 'DISABLED';

/** Schedule fields used by the reusable ScheduleConfigurationSection. */
export type ScheduleConfigurationFormData = {
  schedule_type: ScheduleType;
  rateValue?: number;
  rateUnit?: string;
  rateStartDate?: string;
  rateStartTime?: string;
  rateEndCondition?: ScheduleEndCondition;
  rateEndDate?: string;
  oneTimeDate?: string;
  oneTimeTime?: string;
  recurringFrequency?: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  recurringRepeatEvery?: number;
  recurringDays?: string[];
  recurringStartDate?: string;
  recurringStartTime?: string;
  recurringDayOfMonth?: number | 'l';
  recurringEndCondition?: ScheduleEndCondition;
  recurringEndDate?: string;
  state: ScheduleConfigurationState;
  timezone: string;
};

export const scheduleTypeOptions = {
  RATE: 'Rate',
  Recurring: 'Recurring',
  One_time: 'One-time',
  Immediate: 'Immediate',
} as const;

export type RecurringFrequencyOption = 'DAILY' | 'WEEKLY' | 'MONTHLY';

export const RATE_UNIT_OPTIONS = [
  { key: 'minutes', value: 'minutes', label: 'Minutes' },
  { key: 'hours', value: 'hours', label: 'Hours' },
  { key: 'days', value: 'days', label: 'Days' },
] as const;

export const STATE_OPTIONS = [
  { key: 'ENABLED', value: 'ENABLED', label: 'Enabled' },
  { key: 'DISABLED', value: 'DISABLED', label: 'Disabled' },
] as const;

export const TIMEZONE_OPTIONS = [
  { key: 'UTC', value: 'UTC', label: 'UTC' },
  { key: 'America/New_York', value: 'America/New_York', label: 'America/New_York' },
  { key: 'America/Los_Angeles', value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
  { key: 'America/Chicago', value: 'America/Chicago', label: 'America/Chicago' },
  { key: 'Europe/London', value: 'Europe/London', label: 'Europe/London' },
  { key: 'Europe/Paris', value: 'Europe/Paris', label: 'Europe/Paris' },
  { key: 'Asia/Tokyo', value: 'Asia/Tokyo', label: 'Asia/Tokyo' },
  { key: 'Asia/Kolkata', value: 'Asia/Kolkata', label: 'Asia/Kolkata' },
] as const;

export type ScheduleSelectChangeHandler = (
  onChange: (value: string) => void
) => (keys: unknown) => void;

export type ScheduleConfigurationSectionProps = {
  // Loose control typing so host forms (e.g. settings ScheduleFormData) can reuse this section.
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  getValues: () => ScheduleConfigurationFormData;
  handleSelectChange: ScheduleSelectChangeHandler;
  watchedScheduleType: string;
  use12HourFormat?: boolean;
  showStateAndTimezone?: boolean;
  /** When set, only these schedule type tabs are shown (e.g. hide Rate for notifications). */
  availableScheduleTypes?: ScheduleType[];
  /** When set, limits recurring frequency options (notification catalog mode). */
  availableRecurringFrequencies?: RecurringFrequencyOption[];
  /** When set, limits which recurring frequencies show repeat-every (from catalog grammar). */
  repeatEveryFrequencies?: RecurringFrequencyOption[];
  /** Show timezone for Recurring / One-time when state+timezone block is hidden. */
  showTimezone?: boolean;
  embedded?: boolean;
};

export const defaultScheduleConfigurationValues: ScheduleConfigurationFormData = {
  schedule_type: scheduleTypeOptions.RATE,
  rateValue: 5,
  rateUnit: 'minutes',
  rateStartDate: '',
  rateStartTime: '',
  rateEndCondition: 'never',
  rateEndDate: '',
  oneTimeDate: '',
  oneTimeTime: '',
  recurringFrequency: 'DAILY',
  recurringRepeatEvery: 1,
  recurringDays: [],
  recurringStartDate: '',
  recurringStartTime: '',
  recurringDayOfMonth: 1,
  recurringEndCondition: 'never',
  recurringEndDate: '',
  state: 'ENABLED',
  timezone: 'UTC',
};

export const handleScheduleSelectChange: ScheduleSelectChangeHandler =
  (onChange) => (keys) => {
    const value = Array.from(keys as Set<string>)[0];
    onChange(value || '');
  };
